import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router";

import { AppLayout } from "@/AppLayout.tsx";
import { EmptyPage } from "@/modules/core/components/EmptyPage.tsx";
import { ProjectPage } from "@/modules/projects/components/ProjectPage.js";
import { SavedItemsPage } from "@/modules/saved-items/components/SavedItemsPage.js";
import { GenomicAnnotationsProvider } from "@/modules/summary-pages/GenomicAnnotationsProvider.tsx";
import { AdminPage } from "@/ui-modules/admin/AdminPage.js";
import TracingPage from "@/ui-modules/admin/TracingPage.js";
import DataViewerPage from "@/ui-modules/analytical-viewer/DataViewerPage.js";
import AnalystSessionsPage from "@/ui-modules/content-browser/AnalystSessionsPage.js";
import ContentBrowserPage from "@/ui-modules/content-browser/ContentBrowserPage.js";
import EventsPage from "@/ui-modules/content-browser/EventsPage.js";
import ProjectsListingPage from "@/ui-modules/content-browser/ProjectsListingPage.js";
import VectorsPage from "@/ui-modules/content-browser/VectorsPage.js";
import AccountSettingsPage from "@/ui-modules/core/AccountSettingsPage.js";
import CreateAccountPasswordPage from "@/ui-modules/core/CreateAccountPasswordPage.js";
import {
  BrowserUnsupportedPage,
  DataInProgessPage,
  ErrorPage404,
  LimitedBrowserSupportPage,
} from "@/ui-modules/core/errors/InformationPage.js";
import { RouterRuntimeException } from "@/ui-modules/core/errors/RuntimeException.tsx";
import ErrorsPage from "@/ui-modules/core/ErrorsPage.js";
import { NotAllowed, Welcome } from "@/ui-modules/core/LoginPage.js";
import JobDetailPageLayout from "@/ui-modules/core/navigation-panel/JobDetailPageLayout.js";
import { SearchPage } from "@/ui-modules/core/SearchPage.js";
import { SearchResultPage } from "@/ui-modules/core/SearchResultPage.js";
import DashboardPage from "@/ui-modules/dashboard/DashboardPage.js";
import { AaFeatureDetailPage } from "@/ui-modules/entities/AaFeatureDetailPage.js";
import ContigDetailPage from "@/ui-modules/entities/ContigDetailPage.js";
import { EventDetailsPage } from "@/ui-modules/entities/EventDetailsPage.js";
import { GeneDetailPage } from "@/ui-modules/entities/GeneDetailPage.js";
import { GenomeDetailPage } from "@/ui-modules/entities/GenomeDetailPage.js";
import { MarkerDetailPage } from "@/ui-modules/entities/MarkerDetailPage.js";
import { NuFeatureDetailPage } from "@/ui-modules/entities/NuFeatureDetailPage.js";
import ProteinDetailPage from "@/ui-modules/entities/ProteinDetailPage.js";
import SampleParentDetailPage from "@/ui-modules/entities/SampleParentDetailPage.js";
import { SamplePage } from "@/ui-modules/entities/samples/SamplePage.js";
import SamplesPage from "@/ui-modules/entities/SamplesPage.js";
import { SavedGroupPage } from "@/ui-modules/entities/SavedGroupPage.js";
import SequenceDetailPage from "@/ui-modules/entities/SequenceDetailPage.js";
import TranscriptDetailPage from "@/ui-modules/entities/TranscriptDetailPage.js";
import VariantDetailPage from "@/ui-modules/entities/VariantDetailPage.js";
import { GenomeViewerPage } from "@/ui-modules/genome-viewer/GenomeViewerPage.js";
import { AuthLoginPage } from "@/ui-modules/pages/AuthLoginPage.tsx";
import { RequireAuthentication } from "@/ui-modules/pages/components/RequireAuthentication.tsx";
import { FilesPage } from "@/ui-modules/pages/FilesPage.tsx";
import { PlaybooksPage } from "@/ui-modules/pages/index.js";
import { LogoutPage } from "@/ui-modules/pages/LogoutPage.tsx";

const WorkflowEditorPage = lazy(() => import("@/modules/workflow/WorkflowEditorPage.tsx"));
const WorkflowStartupScreen = lazy(() => import("@/modules/workflow/WorkflowStartupScreen.tsx"));
const GenomeViewerSyntenyPage = lazy(() => import("@/modules/genome-viewer/GenomeViewerPage.tsx"));
const SequenceAnalyzerPage = lazy(() => import("@/modules/sequence-analyzer/SequenceAnalyzerPage.tsx"));

export const getRoutes = () => {
  return (
    <Route path="/" element={<AppLayout />} ErrorBoundary={RouterRuntimeException}>
      <Route index element={<Navigate to="/home" replace />} />
      <Route path="login" element={<AuthLoginPage />} />
      <Route path="logout" element={<LogoutPage />} />
      <Route path="inprogress" element={<DataInProgessPage />} />
      <Route path="unsupported" element={<BrowserUnsupportedPage />} />
      <Route path="limitedBrowserSupport" element={<LimitedBrowserSupportPage />} />
      <Route path="errors" element={<ErrorsPage />} />
      <Route path="trace" element={<TracingPage />} />
      <Route path="notallowed" element={<NotAllowed />} />
      <Route path="welcome" element={<Welcome />} />
      <Route path="createPassword" element={<CreateAccountPasswordPage />} />
      <Route
        path="/*"
        element={
          <RequireAuthentication>
            <Routes>
              <Route path="home" element={<DashboardPage />} />
              <Route path="gene/:geneId/*" element={<GeneDetailPage />} />
              <Route path="protein/:proteinId/*" element={<ProteinDetailPage />} />
              <Route path="transcript/:transcriptId/*" element={<TranscriptDetailPage />} />
              <Route path="contig/:contigId/*" element={<ContigDetailPage />} />
              <Route path="sequence/:sequenceId/*" element={<SequenceDetailPage />} />
              <Route path="probe/:sequenceId/*" element={<SequenceDetailPage />} />
              <Route path="otherDna/:sequenceId/*" element={<SequenceDetailPage />} />
              <Route
                path="organism/:genomeId/*"
                element={
                  <GenomicAnnotationsProvider>
                    <GenomeDetailPage />
                  </GenomicAnnotationsProvider>
                }
              />
              <Route path="variant/:variantId/*" element={<VariantDetailPage />} />
              <Route path="marker/:markerId/*" element={<MarkerDetailPage />} />
              <Route path="nuFeature/:nuFeatureId/*" element={<NuFeatureDetailPage />} />
              <Route path="aaFeature/:aaFeatureId/*" element={<AaFeatureDetailPage />} />
              <Route path="sample/:sampleId/*" element={<SamplePage />} />
              <Route path="parent/:parentId/*" element={<SampleParentDetailPage />} />
              <Route path="event/:eventId/*" element={<EventDetailsPage />} />
              <Route path="project/:projectId/*" element={<ProjectPage />} />
              <Route path="job/:jobId/*" element={<JobDetailPageLayout />} />
              <Route path="search/:searchResultId/*" element={<SearchResultPage />} />
              <Route path="group/*" element={<SavedGroupPage />} />
              <Route path="projects" element={<ProjectsListingPage />} />
              <Route path="content/ssaSessions" element={<AnalystSessionsPage />} />
              <Route path="content/events" element={<EventsPage />} />
              <Route path="content/files/*" element={<FilesPage />} />
              <Route path="content/savedItems/*" element={<SavedItemsPage />} />
              <Route path="content/vectors/*" element={<VectorsPage />} />
              <Route path="content/*" element={<ContentBrowserPage />} />
              <Route path="admin/*" element={<AdminPage />} />
              <Route path="visualizer/data/group/:groupId/*" element={<DataViewerPage />} />
              <Route path="visualizer/data/dataset/:datasetId" element={<DataViewerPage />} />
              <Route path="visualizer/data" element={<DataViewerPage />} />
              <Route path="visualizer/genome" element={<GenomeViewerPage />} />
              <Route path="visualizer/genome/:genomeId" element={<GenomeViewerPage />} />
              <Route
                path="/visualizer/synteny"
                element={
                  <Suspense fallback={<EmptyPage />}>
                    <GenomeViewerSyntenyPage />
                  </Suspense>
                }
              />
              <Route
                path="/visualizer/synteny/:syntenyOrgId"
                element={
                  <Suspense fallback={<EmptyPage />}>
                    <GenomeViewerSyntenyPage />
                  </Suspense>
                }
              />
              <Route
                path="/visualizer/sequence"
                element={
                  <Suspense fallback={<EmptyPage />}>
                    <SequenceAnalyzerPage />
                  </Suspense>
                }
              />
              <Route path="searchQuery/*" element={<SearchPage />} />
              <Route path="samples" element={<SamplesPage />} />
              <Route path="sampleType/:sampleTypeId/samples" element={<SamplesPage />} />
              <Route path="user/*" element={<AccountSettingsPage />} />
              <Route path="playbooks" element={<PlaybooksPage />} />
              <Route
                path={"processor/session/:workflowEventId/*"}
                element={
                  <Suspense fallback={<EmptyPage />}>
                    <WorkflowEditorPage />
                  </Suspense>
                }
              />
              <Route
                path="processor"
                element={
                  <Suspense fallback={<EmptyPage />}>
                    <WorkflowStartupScreen />
                  </Suspense>
                }
              />
              <Route path={"/*"} element={<ErrorPage404 />} />
            </Routes>
          </RequireAuthentication>
        }
      />
    </Route>
  );
};
